import React, { useState, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Check, Copy, Eye, EyeOff, Shield } from "lucide-react";
import Modal from "components/common/Modal";

const ApiKeyCreatedModal = ({ show, onClose, apiKey }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);

  const rawKey = apiKey?.raw_key || apiKey?.key || "";

  const obfuscated = useMemo(() => {
    if (!rawKey) return "";
    const prefix = rawKey.slice(0, 8);
    const suffix = rawKey.slice(-4);
    return `${prefix}${"•".repeat(Math.max(rawKey.length - 12, 8))}${suffix}`;
  }, [rawKey]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rawKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = rawKey;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDone = () => {
    if (!confirmingClose) {
      setConfirmingClose(true);
      return;
    }
    setCopied(false);
    setRevealed(false);
    setConfirmingClose(false);
    onClose();
  };

  const handleClose = () => {
    setCopied(false);
    setRevealed(false);
    setConfirmingClose(false);
    onClose();
  };

  return (
    <Modal show={show} onClose={handleClose} maxWidth="480px">
      {/* Header */}
      <div
        className="relative overflow-hidden p-7"
        style={{
          background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
        }}
      >
        <div
          className="absolute rounded-full"
          style={{
            width: 120,
            height: 120,
            top: -30,
            right: -30,
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 80,
            height: 80,
            bottom: -40,
            right: 60,
            background: "rgba(255, 255, 255, 0.08)",
          }}
        />

        <div className="relative">
          <div
            className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
            style={{ background: "rgba(255, 255, 255, 0.2)" }}
          >
            <Shield size={24} className="text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">
            {t("workspace.apiKeyCreatedModal.title")}
          </h2>
          <p
            className="mt-1.5 text-sm"
            style={{ color: "rgba(255, 255, 255, 0.8)" }}
          >
            {t("workspace.apiKeyCreatedModal.subtitle")}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="p-7">
        {/* Key name badge */}
        <div className="mb-5 flex items-center gap-2">
          <span className="rounded-lg bg-[#F3E8FF] px-2.5 py-1 text-xs font-medium text-[#7C3AED]">
            {apiKey?.name}
          </span>
        </div>

        {/* Key display */}
        <div
          className="rounded-xl p-4"
          style={{
            background: "#1E1E2E",
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <code
              className="min-w-0 flex-1 truncate text-sm tracking-wide"
              style={{ color: "#A5F3AB" }}
            >
              {revealed ? rawKey : obfuscated}
            </code>
            <div className="flex flex-shrink-0 items-center gap-1">
              <button
                onClick={() => setRevealed((prev) => !prev)}
                className="flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-150"
                style={{
                  background: "rgba(255, 255, 255, 0.08)",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.15)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.08)")
                }
                title={
                  revealed
                    ? t("workspace.apiKeyCreatedModal.hideKey")
                    : t("workspace.apiKeyCreatedModal.revealKey")
                }
              >
                {revealed ? (
                  <EyeOff size={15} className="text-[#9CA3AF]" />
                ) : (
                  <Eye size={15} className="text-[#9CA3AF]" />
                )}
              </button>
              <button
                onClick={handleCopy}
                className="flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-150"
                style={{
                  background: copied
                    ? "rgba(16, 185, 129, 0.2)"
                    : "rgba(255, 255, 255, 0.08)",
                }}
                onMouseEnter={(e) => {
                  if (!copied)
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.15)";
                }}
                onMouseLeave={(e) => {
                  if (!copied)
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.08)";
                }}
                title={t("workspace.apiKeyCreatedModal.copyTitle")}
              >
                {copied ? (
                  <Check size={15} className="text-[#10B981]" />
                ) : (
                  <Copy size={15} className="text-[#9CA3AF]" />
                )}
              </button>
            </div>
          </div>
          {copied && (
            <p className="mt-2 text-xs font-medium text-[#10B981]">
              {t("workspace.apiKeyCreatedModal.copied")}
            </p>
          )}
        </div>

        {/* Warning */}
        <div
          className="mt-5 flex gap-3 rounded-xl p-3.5"
          style={{
            background:
              "linear-gradient(135deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 100%)",
            border: "1px solid rgba(232, 133, 74, 0.15)",
          }}
        >
          <p className="text-[12px] leading-relaxed text-[#6B7280]">
            {t("workspace.apiKeyCreatedModal.storeWarning")}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-[#F3F4F6] px-7 py-5">
        {confirmingClose && (
          <div
            className="mb-4 flex gap-3 rounded-xl p-3.5"
            style={{
              background:
                "linear-gradient(135deg, rgba(220, 38, 38, 0.08) 0%, rgba(220, 38, 38, 0.03) 100%)",
              border: "1px solid rgba(220, 38, 38, 0.15)",
            }}
          >
            <p className="text-[12px] leading-relaxed text-[#6B7280]">
              <Trans
                i18nKey="workspace.apiKeyCreatedModal.confirmClose"
                components={{
                  emphasis: <span className="font-medium text-[#DC2626]" />,
                }}
              />
            </p>
          </div>
        )}
        <div className="flex justify-end gap-3">
          {confirmingClose && (
            <button
              onClick={() => setConfirmingClose(false)}
              className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
            >
              {t("workspace.apiKeyCreatedModal.goBack")}
            </button>
          )}
          <button
            onClick={handleDone}
            className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
            style={{
              background: confirmingClose
                ? "#DC2626"
                : "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
              boxShadow: confirmingClose
                ? "0 2px 8px rgba(220, 38, 38, 0.3)"
                : "0 2px 8px rgba(224, 122, 58, 0.3)",
            }}
          >
            {confirmingClose
              ? t("workspace.apiKeyCreatedModal.confirmCloseButton")
              : t("common.actions.done")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ApiKeyCreatedModal;
